import { useCustom } from '@refinedev/core';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate } from '@/lib/format';
import type { AuditEntry } from '@/lib/types';

interface Props {
  accountId: string;
}

interface AuditResponse {
  data: AuditEntry[];
  count: number;
}

const ACTION_LABEL: Record<string, string> = {
  account_created: 'Conta criada',
  account_imported: 'Conta importada',
  credentials_revealed: 'Credenciais reveladas',
  recovery_code_assigned: 'Code atribuído',
  recovery_code_unassigned: 'Code desatribuído',
  recovery_code_marked_used: 'Code marcado como usado',
  recovery_code_marked_lost: 'Code marcado como perdido',
  status_changed: 'Status alterado',
  topup_applied: 'Top-up aplicado',
  note_added: 'Nota atualizada',
  suspended: 'Suspensa',
  banned: 'Banida',
  retired: 'Aposentada',
};

export function AuditTab({ accountId }: Props) {
  const { data, isLoading } = useCustom<AuditResponse>({
    url: '/audit-log',
    method: 'get',
    config: { query: { account_id: accountId, limit: 200 } },
  });

  if (isLoading) return <Skeleton className="h-64 w-full" />;

  const entries = (data?.data?.data ?? []) as AuditEntry[];

  if (!entries.length) {
    return (
      <div className="rounded-md border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
        Sem ações registradas pra esta conta ainda.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">{entries.length} ações registradas.</p>
      <ul className="relative space-y-3 border-l border-border/50 pl-5">
        {entries.map((entry) => (
          <li key={entry.id} className="relative">
            <span className="absolute -left-[27px] top-1.5 h-3 w-3 rounded-full border-2 border-border bg-background" />
            <div className="rounded-md border bg-card p-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <span className="text-sm font-medium">
                    {ACTION_LABEL[entry.action] ?? entry.action}
                  </span>
                  <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
                    {entry.actor_type}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">{formatDate(entry.created_at)}</span>
              </div>
              {entry.metadata && Object.keys(entry.metadata).length > 0 && (
                <pre className="mt-2 max-h-40 overflow-auto rounded bg-muted/50 p-2 font-mono text-[11px] text-muted-foreground">
                  {JSON.stringify(entry.metadata, null, 2)}
                </pre>
              )}
              {entry.ip && (
                <p className="mt-1 font-mono text-[11px] text-muted-foreground">IP: {entry.ip}</p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
